import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Star, BarChart3, CheckCircle, AlertTriangle, MessageSquare, LogOut } from 'lucide-react';
import Swal from 'sweetalert2';
import URLS from '../config/urls';
import clientDetails from '../constants/clientDetails';
import axiosInstance from '../config/axiosConfig';

const FeedbackForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    user_id: null,
    answers: []
  });

  const [currentAnswers, setCurrentAnswers] = useState({});
  const [questionsData, setQuestionsData] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [sliderValues, setSliderValues] = useState({});
  const [textErrors, setTextErrors] = useState({});
  const [mandatoryErrors, setMandatoryErrors] = useState({});

  const TEXT_LIMITS = {
    SINGLE_LINE: 500,
    MULTI_LINE: 200,
    OTHER_SPECIFY: 250
  };

  const validateTextInput = (text) => {
    const validPattern = /^[a-zA-Z0-9\s.,!?:;\-()\[\]{}'"\n\r]*$/;
    return validPattern.test(text);
  };

  const sanitizeTextInput = (text) => {
    return text.replace(/[^a-zA-Z0-9\s.,!?:;\-()\[\]{}'"]/g, '');
  };

  const isOnlyWhitespace = (text) => {
    return text.trim().length === 0;
  };

  const handleTextInput = (text) => {
    if (!validateTextInput(text)) {
      text = sanitizeTextInput(text);
    }
    return text;
  };

  useEffect(() => {
    handleAuthentication();
  }, []);

  const handleAuthentication = async () => {
    setLoading(true);

    try {
      const authCode = searchParams.get('code');
      const errorCode = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');

      if (errorCode) {
        setError({
          errorCode: errorCode,
          errorMsg: errorDescription || 'Authentication failed'
        });
        setLoading(false);
        setTimeout(() => navigate('/', { replace: true }), 3000);
        return;
      }

      if (authCode) {
        await getUserDetails(authCode);
        return;
      }
    } catch (error) {
      console.error('Authentication error:', error);
      setError({
        errorCode: 'auth_error',
        errorMsg: error.message || 'Authentication failed'
      });
      setLoading(false);
      setTimeout(() => navigate('/', { replace: true }), 3000);
    }
  };

  const getUserDetails = async (authCode) => {
    setError(null);
    setUserData(null);

    try {
      const client_id = clientDetails.clientId;
      const redirect_uri = clientDetails.redirect_uri_userprofile;
      const grant_type = clientDetails.grant_type;

      const tokenParams = new URLSearchParams({
        code: authCode,
        client_id: client_id,
        redirect_uri: redirect_uri,
        grant_type: grant_type
      });

      const tokenResponse = await axiosInstance.post(
        `${URLS.ESIGNET_TOKEN}?${tokenParams}`,
        null,
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          }
        }
      );

      const accessToken = tokenResponse.data.access_token;

      const userInfoParams = new URLSearchParams({
        access_token: accessToken
      });

      const userInfoResponse = await axiosInstance.post(
        `${URLS.ESIGNET_SAVE_USER}?${userInfoParams}`,
        null,
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          }
        }
      );

      const userId = userInfoResponse.data.userid;

      if (!userId) {
        throw new Error('No user ID received from authentication');
      }

      setUserData({
        uniqueId: userId,
        loginTime: new Date().toISOString()
      });
      setFormData(prev => ({ ...prev, user_id: userId }));
      setIsAuthenticated(true);

      await fetchQuestions();

    } catch (error) {
      const errorMsg = error.response?.data?.error || error.message;
      setError({ errorCode: '', errorMsg });
      setLoading(false);
      setTimeout(() => navigate('/', { replace: true }), 3000);
    } finally {
      setLoading(false);
    }
  };

  const fetchQuestions = async () => {
    try {
      const adminToken = sessionStorage.getItem('adminToken');
      if (!adminToken) {
        throw new Error('Admin session expired. Please ask admin to login again.');
      }

      const response = await axiosInstance.get(URLS.GET_QUESTIONS);
      const data = response.data;

      // Sort questions based on sort_order
      const sortedData = [...data].sort((a, b) => a.sort_order - b.sort_order);

      setQuestionsData(sortedData);

      const initialSliderValues = {};
      sortedData.forEach(question => {
        if (question.answer_type === 'SCALE') {
          initialSliderValues[`q${question.question_id}`] = 4;

          const defaultAnswerId = question.answers_list[3].answer_id;
          setCurrentAnswers(prev => ({
            ...prev,
            [`q${question.question_id}`]: { answer_id: defaultAnswerId, typed_answer: '' }
          }));
        }
      });
      setSliderValues(initialSliderValues);

    } catch (error) {
      console.error('Error fetching questions:', error);

      if (error.response?.status === 401) {
        setError({
          errorCode: 'auth_error',
          errorMsg: 'Admin session expired. Please ask the administrator to log in again.'
        });

        setTimeout(() => navigate('/', { replace: true }), 3000);
      } else {
        setError({
          errorCode: 'fetch_error',
          errorMsg: error.response?.data?.message || 'Failed to load questions'
        });
      }
    }
  };

  const getQuestionIcon = (questionId) => {
    const icons = {
      1: <Star className="q-icon" />,
      2: <BarChart3 className="q-icon" />,
      3: <BarChart3 className="q-icon" />,
      4: <CheckCircle className="q-icon" />,
      5: <MessageSquare className="q-icon" />,
      6: <AlertTriangle className="q-icon" />,
      7: <MessageSquare className="q-icon" />
    };
    return icons[questionId] || <MessageSquare className="q-icon" />;
  };

  const handleSliderChange = (questionId, value, answerId) => {
    setSliderValues(prev => ({
      ...prev,
      [`q${questionId}`]: value
    }));

    setCurrentAnswers(prev => ({
      ...prev,
      [`q${questionId}`]: {
        answer_id: answerId,
        typed_answer: ''
      }
    }));

    setMandatoryErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[`q${questionId}`];
      return newErrors;
    });
  };

  const handleRadioChange = (questionId, answerId, typedAnswer = '') => {
    const processedText = handleTextInput(typedAnswer);

    setCurrentAnswers(prev => ({
      ...prev,
      [`q${questionId}`]: {
        answer_id: answerId,
        typed_answer: processedText
      }
    }));

    const charLimit = TEXT_LIMITS.OTHER_SPECIFY;

    if (typedAnswer && isOnlyWhitespace(processedText)) {
      setTextErrors(prev => ({
        ...prev,
        [`q${questionId}`]: 'Please enter valid text (not just spaces)'
      }));
    } else if (processedText.length > charLimit) {
      setTextErrors(prev => ({
        ...prev,
        [`q${questionId}`]: `Text must not exceed ${charLimit} characters`
      }));
    } else {
      setTextErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`q${questionId}`];
        return newErrors;
      });
    }

    setMandatoryErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[`q${questionId}`];
      return newErrors;
    });
  };

  const handleCheckboxChange = (questionId, answerId, isChecked) => {
    setCurrentAnswers(prev => {
      const currentArray = prev[`q${questionId}`] || [];

      if (isChecked) {
        return {
          ...prev,
          [`q${questionId}`]: [...currentArray, { answer_id: answerId, typed_answer: '' }]
        };
      } else {
        const filtered = currentArray.filter(item => item.answer_id !== answerId);

        setTextErrors(prevErrors => {
          const newErrors = { ...prevErrors };
          delete newErrors[`q${questionId}_${answerId}`];
          return newErrors;
        });

        return {
          ...prev,
          [`q${questionId}`]: filtered
        };
      }
    });

    if (isChecked) {
      setMandatoryErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`q${questionId}`];
        return newErrors;
      });
    }
  };

  const handleCheckboxTextChange = (questionId, answerId, typedAnswer) => {
    const processedText = handleTextInput(typedAnswer);

    setCurrentAnswers(prev => {
      const currentArray = prev[`q${questionId}`] || [];
      const updatedArray = currentArray.map(item =>
        item.answer_id === answerId
          ? { ...item, typed_answer: processedText }
          : item
      );

      return {
        ...prev,
        [`q${questionId}`]: updatedArray
      };
    });

    const charLimit = TEXT_LIMITS.OTHER_SPECIFY;

    if (isOnlyWhitespace(processedText)) {
      setTextErrors(prev => ({
        ...prev,
        [`q${questionId}_${answerId}`]: 'Please enter valid text (not just spaces)'
      }));
    } else if (processedText.length > charLimit) {
      setTextErrors(prev => ({
        ...prev,
        [`q${questionId}_${answerId}`]: `Text must not exceed ${charLimit} characters`
      }));
    } else {
      setTextErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`q${questionId}_${answerId}`];
        return newErrors;
      });
    }
  };

  const handleTextChange = (questionId, answerId, typedAnswer) => {
    const processedText = handleTextInput(typedAnswer);

    setCurrentAnswers(prev => ({
      ...prev,
      [`q${questionId}`]: {
        answer_id: answerId,
        typed_answer: processedText
      }
    }));

    const charLimit = TEXT_LIMITS.MULTI_LINE;

    if (isOnlyWhitespace(processedText)) {
      setTextErrors(prev => ({
        ...prev,
        [`q${questionId}`]: 'Please enter valid text (not just spaces)'
      }));
    } else if (processedText.length > charLimit) {
      setTextErrors(prev => ({
        ...prev,
        [`q${questionId}`]: `Text must not exceed ${charLimit} characters`
      }));
    } else {
      setTextErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`q${questionId}`];
        return newErrors;
      });
    }

    setMandatoryErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[`q${questionId}`];
      return newErrors;
    });
  };

  const validateForm = () => {
    const newTextErrors = {};
    const newMandatoryErrors = {};

    questionsData.forEach(question => {
      const { question_id, answer_type, is_mandatory, answers_list } = question;
      const answer = currentAnswers[`q${question_id}`];

      // Treat all questions as mandatory
      if (!answer || (Array.isArray(answer) && answer.length === 0)) {
        newMandatoryErrors[`q${question_id}`] = 'This question is mandatory';
      } else if (answer_type === 'RADIO_WITH_TEXT' || answer_type === 'TEXT') {
        const hasTextAnswer = answers_list.some(a => a.has_text_answer && a.answer_id === answer.answer_id);

        if (hasTextAnswer && (!answer.typed_answer || isOnlyWhitespace(answer.typed_answer))) {
          newTextErrors[`q${question_id}`] = 'Please provide a text response';
        }
      } else if (answer_type === 'CHECKBOX_WITH_TEXT' && Array.isArray(answer)) {
        answer.forEach(item => {
          const answerOption = answers_list.find(a => a.answer_id === item.answer_id);
          if (answerOption?.has_text_answer && (!item.typed_answer || isOnlyWhitespace(item.typed_answer))) {
            newTextErrors[`q${question_id}_${item.answer_id}`] = 'Please provide a text response';
          }
        });
      }

      if (answer_type === 'RADIO_WITH_TEXT' && answer?.typed_answer) {
        const charLimit = TEXT_LIMITS.OTHER_SPECIFY;
        if (answer.typed_answer.length > charLimit) {
          newTextErrors[`q${question_id}`] = `Text must not exceed ${charLimit} characters`;
        }
      } else if (answer_type === 'TEXT' && answer?.typed_answer) {
        const charLimit = TEXT_LIMITS.MULTI_LINE;
        if (answer.typed_answer.length > charLimit) {
          newTextErrors[`q${question_id}`] = `Text must not exceed ${charLimit} characters`;
        }
      } else if (answer_type === 'CHECKBOX_WITH_TEXT' && Array.isArray(answer)) {
        const charLimit = TEXT_LIMITS.OTHER_SPECIFY;
        answer.forEach(item => {
          if (item.typed_answer && item.typed_answer.length > charLimit) {
            newTextErrors[`q${question_id}_${item.answer_id}`] = `Text must not exceed ${charLimit} characters`;
          }
        });
      }
    });

    setTextErrors(newTextErrors);
    setMandatoryErrors(newMandatoryErrors);

    return Object.keys(newTextErrors).length === 0 && Object.keys(newMandatoryErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      Swal.fire({
        icon: 'warning',
        title: 'Incomplete Form',
        text: 'Please fill in all required fields and fix any errors before submitting.',
        confirmButtonColor: '#1565C0',
        confirmButtonText: 'OK'
      });
      return;
    }

    setSubmitting(true);

    try {
      const answers = Object.entries(currentAnswers).map(([key, value]) => {
        if (Array.isArray(value)) {
          return value.map(item => ({
            answer_id: item.answer_id,
            typed_answer: item.typed_answer || ''
          }));
        }

        return {
          answer_id: value.answer_id,
          typed_answer: value.typed_answer || ''
        };
      }).flat();

      const submissionData = {
        user_id: formData.user_id,
        answers: answers
      };

      await axiosInstance.post(URLS.SUBMIT_FEEDBACK, submissionData);

      await Swal.fire({
        icon: 'success',
        title: 'Thank You!',
        html: `
          <p style="font-size: 16px; margin: 20px 0;">Your feedback has been submitted successfully.</p>
          <p style="font-size: 14px; color: #666;">We appreciate your time and valuable input.</p>
        `,
        confirmButtonColor: '#1565C0',
        confirmButtonText: 'Close',
        allowOutsideClick: false
      });

      navigate('/', { replace: true });

    } catch (error) {
      console.error('Error submitting feedback:', error);

      Swal.fire({
        icon: 'error',
        title: 'Submission Failed',
        text: error.response?.data?.message || 'Failed to submit feedback. Please try again.',
        confirmButtonColor: '#1565C0',
        confirmButtonText: 'OK'
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Slider drag logic
  const sliderRefs = React.useRef({});
  const isDragging = React.useRef(false);

  const handlePointerDown = (e, questionId, answersList) => {
    isDragging.current = true;
    e.target.setPointerCapture(e.pointerId);
    handlePointerMove(e, questionId, answersList);
  };

  const handlePointerMove = (e, questionId, answersList) => {
    if (!isDragging.current) return;

    const slider = sliderRefs.current[questionId];
    if (!slider) return;

    const rect = slider.getBoundingClientRect();
    const percentage = Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1);

    // Calculate nearest step (1 to 5)
    const steps = answersList.length;
    const stepIndex = Math.round(percentage * (steps - 1));
    const value = stepIndex + 1;
    const answerId = answersList[stepIndex].answer_id;

    if (sliderValues[`q${questionId}`] !== value) {
      handleSliderChange(questionId, value, answerId);
    }
  };

  const handlePointerUp = (e) => {
    isDragging.current = false;
    e.target.releasePointerCapture(e.pointerId);
  };


  const renderQuestion = (question, index) => {
    const { question_id, question: question_text, answer_type, answers_list, is_mandatory, char_limit } = question;
    const charLimit = char_limit || (answer_type === 'TEXT' ? TEXT_LIMITS.MULTI_LINE : TEXT_LIMITS.OTHER_SPECIFY);

    return (
      <div key={question_id} className="question-box">
        <div className="q-header">
          <div className="q-icon-container">
            <div className="q-icon-bg"></div>
            {getQuestionIcon(question_id)}
          </div>
          <div className="q-content">
            <h3 className="q-title">
              {question_text}
              <span className="required-mark">*</span>
            </h3>
          </div>
        </div>

        {mandatoryErrors[`q${question_id}`] && (
          <div className="error-box">
            <AlertTriangle className="error-icon" />
            <span>{mandatoryErrors[`q${question_id}`]}</span>
          </div>
        )}

        <div className="q-body">
          {answer_type === 'SCALE' && (
            <div className="scale-section">
              <div
                className="custom-slider-container"
                ref={el => sliderRefs.current[question_id] = el}
                onPointerDown={(e) => handlePointerDown(e, question_id, answers_list)}
                onPointerMove={(e) => handlePointerMove(e, question_id, answers_list)}
                onPointerUp={handlePointerUp}
                onPointerLeave={handlePointerUp}
                style={{ touchAction: 'none' }} // Prevent scrolling while dragging
              >
                <div className="custom-slider-track"></div>
                <div
                  className="custom-slider-fill"
                  style={{ width: `${((sliderValues[`q${question_id}`] || 4) - 1) * 25}%` }}
                ></div>
                <div
                  className="custom-slider-thumb"
                  style={{ left: `${((sliderValues[`q${question_id}`] || 4) - 1) * 25}%` }}
                ></div>
                {answers_list.map((answer, idx) => (
                  <div
                    key={answer.answer_id}
                    className="custom-slider-point"
                    style={{ left: `${idx * 25}%` }}
                  >
                    <div className={`point-dot ${sliderValues[`q${question_id}`] === idx + 1 ? 'active' : ''}`}></div>
                  </div>
                ))}
              </div>
              <div className="scale-marks">
                {answers_list.map((answer, idx) => (
                  <div
                    key={answer.answer_id}
                    className={`scale-mark ${sliderValues[`q${question_id}`] === idx + 1 ? 'active' : ''}`}
                    style={{ left: `${idx * 25}%`, transform: 'translateX(-50%)', position: 'absolute' }}
                    onClick={() => {
                      const value = idx + 1;
                      handleSliderChange(question_id, value, answer.answer_id);
                    }}
                  >
                    <span className="mark-label">{answer.answer}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {answer_type === 'RADIO' && (
            <div className="options-list">
              {answers_list.map((answer) => (
                <div key={answer.answer_id} className="option-wrapper">
                  <label className="option-item">
                    <input
                      type="radio"
                      name={`q${question_id}`}
                      value={answer.answer_id}
                      onChange={() => handleRadioChange(question_id, answer.answer_id)}
                      className="radio-input"
                    />
                    <span className="radio-custom"></span>
                    <span className="option-label">{answer.answer}</span>
                  </label>
                </div>
              ))}
            </div>
          )}

          {answer_type === 'RADIO_WITH_TEXT' && (
            <div className="options-list">
              {answers_list.map((answer) => (
                <div key={answer.answer_id} className="option-wrapper">
                  <label className="option-item">
                    <input
                      type="radio"
                      name={`q${question_id}`}
                      value={answer.answer_id}
                      onChange={() => handleRadioChange(question_id, answer.answer_id)}
                      className="radio-input"
                    />
                    <span className="radio-custom"></span>
                    <span className="option-label">{answer.answer}</span>
                  </label>

                  {answer.has_text_answer && currentAnswers[`q${question_id}`]?.answer_id === answer.answer_id && (
                    <div className="text-field-wrapper">
                      <input
                        type="text"
                        placeholder="Please specify..."
                        value={currentAnswers[`q${question_id}`]?.typed_answer || ''}
                        onChange={(e) => handleRadioChange(question_id, answer.answer_id, e.target.value)}
                        className={`text-field ${textErrors[`q${question_id}`] ? 'has-error' : ''}`}
                      />
                      {charLimit && (
                        <div className={`char-count ${(currentAnswers[`q${question_id}`]?.typed_answer || '').length > charLimit ? 'exceeded' : ''
                          }`}>
                          {(currentAnswers[`q${question_id}`]?.typed_answer || '').length}/{charLimit}
                        </div>
                      )}
                      {textErrors[`q${question_id}`] && (
                        <div className="field-error">{textErrors[`q${question_id}`]}</div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {answer_type === 'CHECKBOX_WITH_TEXT' && (
            <div className="options-list">
              {answers_list.map((answer) => {
                const currentCheckboxAnswer = currentAnswers[`q${question_id}`]?.find?.(
                  ans => ans.answer_id === answer.answer_id
                );
                const currentText = currentCheckboxAnswer?.typed_answer || '';

                return (
                  <div key={answer.answer_id} className="option-wrapper">
                    <label className="option-item">
                      <input
                        type="checkbox"
                        onChange={(e) => handleCheckboxChange(question_id, answer.answer_id, e.target.checked)}
                        className="checkbox-input"
                      />
                      <span className="checkbox-custom"></span>
                      <span className="option-label">{answer.answer}</span>
                    </label>
                    {answer.has_text_answer && currentAnswers[`q${question_id}`]?.some(ans => ans.answer_id === answer.answer_id) && (
                      <div className="text-field-wrapper">
                        <input
                          type="text"
                          placeholder="Please specify..."
                          value={currentText}
                          onChange={(e) => handleCheckboxTextChange(question_id, answer.answer_id, e.target.value)}
                          className={`text-field ${textErrors[`q${question_id}_${answer.answer_id}`] ? 'has-error' : ''}`}
                        />
                        {charLimit && (
                          <div className={`char-count ${currentText.length > charLimit ? 'exceeded' : ''}`}>
                            {currentText.length}/{charLimit}
                          </div>
                        )}
                        {textErrors[`q${question_id}_${answer.answer_id}`] && (
                          <div className="field-error">{textErrors[`q${question_id}_${answer.answer_id}`]}</div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {answer_type === 'TEXT' && (
            <div className="textarea-wrapper">
              <textarea
                placeholder="Let us know about your experience..."
                value={currentAnswers[`q${question_id}`]?.typed_answer || ''}
                onChange={(e) => handleTextChange(question_id, answers_list[0].answer_id, e.target.value)}
                className={`textarea-field ${textErrors[`q${question_id}`] ? 'has-error' : ''}`}
                rows="4"
              />
              {charLimit && (
                <div className={`char-count ${(currentAnswers[`q${question_id}`]?.typed_answer || '').length > charLimit ? 'exceeded' : ''
                  }`}>
                  {(currentAnswers[`q${question_id}`]?.typed_answer || '').length}/{charLimit}
                </div>
              )}
              {textErrors[`q${question_id}`] && (
                <div className="field-error">{textErrors[`q${question_id}`]}</div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="feedback-page">
      <div className="feedback-content">
        {/* Header */}
        <div className="feedback-header-card">
          <div className="header-inner">
            <div className="header-logos-group">
              <img
                src="/assets/images/deec-logo.png"
                alt="DEEC Logo"
                className="deec-img"
              />
            </div>

            <div className="header-text-group">
              <h1 className="page-title">Thank you for visiting the Experience Centre</h1>
              <p className="page-subtitle">
                Your feedback will help us improve Sri Lanka's New Digital Economy Experience Center (DEEC) and
                the services offered to citizens.
              </p>
            </div>
          </div>
        </div>

        {/* Questions */}
        <div className="questions-container">
          {questionsData.length > 0 ? (
            questionsData.map((question, index) => renderQuestion(question, index))
          ) : (
            <div className="no-data">
              <MessageSquare className="no-data-icon" />
              <p>No questions available. Please contact the administrator.</p>
            </div>
          )}
        </div>

        {/* Submit Section */}
        {questionsData.length > 0 && (
          <div className="submit-area">
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="btn-submit-feedback"
            >
              {submitting ? (
                <div className="btn-loading">
                  <div className="loading-spinner"></div>
                  <span>Submitting...</span>
                </div>
              ) : (
                <>
                  <span className="btn-text">Submit Feedback</span>
                  <div className="btn-shine"></div>
                </>
              )}
            </button>

            {(Object.keys(textErrors).length > 0 || Object.keys(mandatoryErrors).length > 0) && (
              <div className="submit-warning">
                <AlertTriangle className="warning-icon" />
                <span>Please fill all required fields before submitting.</span>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="feedback-footer-card">
          <div className="footer-divider"></div>
          <p className="footer-text">Powered by SLUDI - Sri Lanka's Digital Identity</p>
        </div>
      </div>
    </div>
  );
};

export default FeedbackForm;